import { useEffect, useState } from "react";
import { api, formatApiError, downloadPdf } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Upload, Download, Trash2, Receipt } from "lucide-react";
import { toast } from "sonner";

function formatMois(m) {
  if (!m) return "—";
  const [y, mo] = m.split("-");
  const noms = ["janvier","février","mars","avril","mai","juin","juillet","août","septembre","octobre","novembre","décembre"];
  return `${noms[parseInt(mo,10)-1] || mo} ${y}`;
}

export default function RHPayslips() {
  const [items, setItems] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ employee_id: "", mois: "", file: null });
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    try {
      const [ps, emp] = await Promise.all([api.get("/payslips"), api.get("/users")]);
      setItems(ps.data);
      setEmployees(emp.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.file) { toast.error("Sélectionnez un fichier PDF"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("employee_id", form.employee_id);
      fd.append("mois", form.mois);
      fd.append("file", form.file);
      await api.post("/payslips", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Bulletin déposé avec succès");
      setOpen(false);
      setForm({ employee_id: "", mois: "", file: null });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setUploading(false); }
  };

  const dl = async (p) => {
    try { await downloadPdf(`/payslips/${p.id}/download`, p.original_filename); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("Supprimer ce bulletin ?")) return;
    try { await api.delete(`/payslips/${id}`); toast.success("Bulletin supprimé"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div>
      <PageHeader
        title="Bulletins de paie"
        subtitle="Déposez les bulletins de paie mensuels des employés."
        action={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="upload-payslip-button" className="bg-[#0066FF] hover:bg-[#0052CC]"><Upload className="h-4 w-4 mr-2" />Déposer un bulletin</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Déposer un bulletin de paie</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <Label>Employé</Label>
                  <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
                    <SelectTrigger data-testid="payslip-employee-select"><SelectValue placeholder="Sélectionner un employé" /></SelectTrigger>
                    <SelectContent>
                      {employees.map((u) => <SelectItem key={u.id} value={u.id}>{u.full_name} — {u.email}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Mois</Label>
                  <Input data-testid="payslip-month-input" type="month" required value={form.mois} onChange={(e) => setForm({ ...form, mois: e.target.value })} />
                </div>
                <div>
                  <Label>Fichier PDF (max 10 Mo)</Label>
                  <Input data-testid="payslip-file-input" type="file" accept="application/pdf" required onChange={(e) => setForm({ ...form, file: e.target.files?.[0] })} />
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
                  <Button data-testid="submit-payslip-button" type="submit" disabled={uploading || !form.employee_id} className="bg-[#0066FF] hover:bg-[#0052CC]">{uploading ? "Envoi…" : "Déposer"}</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      <Card className="border-slate-200 overflow-hidden">
        {items.length === 0 ? (
          <div className="p-12 text-center">
            <Receipt className="h-10 w-10 text-slate-300 mx-auto" />
            <p className="mt-3 text-sm text-slate-500">Aucun bulletin déposé</p>
          </div>
        ) : (
          <>
          <div className="hidden md:block">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Employé</TableHead>
                <TableHead>Mois</TableHead>
                <TableHead>Fichier</TableHead>
                <TableHead>Ajouté le</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((p) => (
                <TableRow key={p.id} data-testid={`rh-payslip-row-${p.id}`}>
                  <TableCell>
                    <div className="font-medium">{p.user_name}</div>
                    <div className="text-xs text-slate-500">{p.user_email}</div>
                  </TableCell>
                  <TableCell className="capitalize">{formatMois(p.mois)}</TableCell>
                  <TableCell className="text-sm text-slate-600">{p.original_filename}</TableCell>
                  <TableCell className="text-sm text-slate-500">{new Date(p.created_at).toLocaleDateString("fr-FR")}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex gap-2 justify-end">
                      <Button size="sm" variant="outline" onClick={() => dl(p)}><Download className="h-3.5 w-3.5" /></Button>
                      <Button data-testid={`delete-payslip-${p.id}`} size="sm" variant="outline" className="text-rose-600 border-rose-200 hover:bg-rose-50" onClick={() => del(p.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </div>
          <div className="md:hidden divide-y divide-slate-100">
            {items.map((p) => (
              <div key={p.id} className="p-4" data-testid={`rh-payslip-card-${p.id}`}>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="min-w-0">
                    <div className="font-medium text-slate-900 truncate">{p.user_name}</div>
                    <div className="text-xs text-slate-500 truncate">{p.user_email}</div>
                  </div>
                  <div className="text-xs text-slate-500 capitalize font-semibold shrink-0">{formatMois(p.mois)}</div>
                </div>
                <div className="text-[11px] text-slate-500 truncate mb-3">{p.original_filename}</div>
                <div className="grid grid-cols-2 gap-2">
                  <Button size="sm" variant="outline" onClick={() => dl(p)} className="w-full"><Download className="h-3.5 w-3.5 mr-1" />Télécharger</Button>
                  <Button data-testid={`delete-payslip-m-${p.id}`} size="sm" variant="outline"
                    className="text-rose-600 border-rose-200 hover:bg-rose-50 w-full" onClick={() => del(p.id)}>
                    <Trash2 className="h-3.5 w-3.5 mr-1" />Supprimer
                  </Button>
                </div>
              </div>
            ))}
          </div>
          </>
        )}
      </Card>
    </div>
  );
}
